import type { ProductCenterProductRow } from "@adpilot/shared";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { MarketplaceBadge } from "@/components/shared/marketplace-badge";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { DashboardCard, DashboardCardHeader, DashboardCardLink } from "./dashboard-card";
import { wholeCurrencyOptions } from "./overview-format";

export interface TopAsinRow {
  /** Stable row key (an ASIN can repeat across shops until the API aggregates by ASIN). */
  key: string;
  asin: string;
  title: string | null;
  imageUrl: string | null;
  isParent: boolean;
  marketplaces: string[];
  totalSales: number | null;
  units: number | null;
  spSales: number | null;
  sdSales: number | null;
  adSpend: number;
  /** Ratios (0.235 = 23.5%). */
  acos: number | null;
  tacos: number | null;
}

/** Product Center rows (ASIN or parent-ASIN dimension) mapped 1:1 to table rows. */
export function topAsinRowsFromProductCenter(
  rows: readonly ProductCenterProductRow[],
  marketplaceByShop?: ReadonlyMap<string, string>,
): TopAsinRow[] {
  return rows.map((row) => {
    const sellerSales = row.seller.status === "unavailable" ? null : row.seller.sales;
    const marketplace = row.shop ? marketplaceByShop?.get(row.shop.id) : undefined;
    return {
      key: `${row.shop?.id ?? ""}:${row.identity.key}`,
      asin: row.identity.asin ?? row.identity.parentAsin ?? row.identity.key,
      title: row.identity.title,
      imageUrl: row.identity.imageUrl,
      isParent: row.identity.dimension === "PARENT_ASIN",
      marketplaces: marketplace ? [marketplace] : [],
      totalSales: sellerSales,
      units: row.seller.status === "unavailable" ? null : row.seller.units,
      // Product Center ad metrics come from Sponsored Products advertised-product reports.
      spSales: row.ads.status === "unavailable" ? null : row.ads.adSales,
      // FIXME: no SD per-product source yet.
      sdSales: null,
      adSpend: row.ads.spend,
      acos: row.ads.acos,
      tacos:
        row.tacosEligible !== false && sellerSales !== null && sellerSales > 0
          ? row.ads.spend / sellerSales
          : null,
    };
  });
}

// Lane widths include the 12px column gap. Full table: 916px; SP/SD sales (P3)
// drop first, then the table scrolls at 716px with the Product column pinned.
const HIDE_P3 = "@max-[916px]:hidden";
const HEAD =
  "h-8 bg-table-footer px-0 pl-3 text-right text-xs font-medium tracking-normal text-muted-foreground";
const CELL = "py-2 pr-0 pl-3 text-right text-xs tabular-nums text-ink";

export interface TopAsinsCardProps {
  rows: readonly TopAsinRow[];
  /** Products matching the scope, for "View all N". */
  totalCount: number;
  /** Shops in the analytics scope (subtitle). */
  shopCount: number;
  /** Applied dashboard ASIN filter; "View all" opens Product Center with it. */
  asins?: readonly string[];
  currency: string | null;
  /** ACoS above this ratio is highlighted; no highlight when null. */
  targetAcos: number | null;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /**
   * The scope has vendor (1P) shops (#679): their total sales, units and TACoS
   * cells stay "—" until Vendor Central lands, and the card says so.
   */
  vendorDataPending?: boolean;
  className?: string;
}

/** Paper "Card · Top ASINs": product identity + sales/ads columns with container-width narrowing. */
export function TopAsinsCard({
  rows,
  totalCount,
  asins,
  shopCount,
  currency,
  targetAcos,
  isLoading = false,
  isError = false,
  onRetry,
  vendorDataPending = false,
  className,
}: TopAsinsCardProps) {
  const { t } = useTranslation("dashboard");
  const { formatNumber, formatPercent } = useFormatters();
  const money = (value: number | null) =>
    value === null ? "—" : formatNumber(value, wholeCurrencyOptions(currency));
  const percent = (value: number | null) => (value === null ? "—" : formatPercent(value, 1));

  return (
    <DashboardCard aria-labelledby="overview-top-asins-title" className={className}>
      <DashboardCardHeader
        titleId="overview-top-asins-title"
        title={t("overview.topAsins.title")}
        meta={shopCount > 1 ? t("overview.topAsins.subtitle", { count: shopCount }) : undefined}
        action={
          <DashboardCardLink
            to="/all-products"
            search={
              asins && asins.length > 0
                ? // Filtered ASINs may be inactive listings; show them anyway.
                  { dimension: "ASIN", asins: [...asins], active: "all" }
                : { dimension: "ASIN" }
            }
          >
            {t("overview.topAsins.viewAll", { count: totalCount })}
          </DashboardCardLink>
        }
      />
      {vendorDataPending ? (
        <p
          data-slot="vendor-data-note"
          className="border-t border-line px-5 py-2 text-xs text-muted-foreground"
        >
          {t("overview.vendorData.topAsinsNote")}
        </p>
      ) : null}
      {isError && rows.length === 0 ? (
        <div className="border-t border-line px-5 py-3">
          <ErrorState
            variant="inline"
            message={t("overview.topAsins.loadFailed")}
            onRetry={onRetry}
          />
        </div>
      ) : (
        <Table className="min-w-[716px] table-fixed">
          <TableHeader>
            <TableRow className="h-8 border-y border-line hover:bg-transparent">
              <TableHead className={cn(HEAD, "sticky left-0 z-[1] pl-5 text-left")}>
                {t("overview.topAsins.columns.product")}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[108px]")}>
                {t("overview.topAsins.columns.totalSales")}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[76px]")}>
                {t("overview.topAsins.columns.units")}
              </TableHead>
              <TableHead className={cn(HEAD, HIDE_P3, "w-[100px]")}>
                {t("overview.topAsins.columns.spSales")}
              </TableHead>
              <TableHead className={cn(HEAD, HIDE_P3, "w-[100px]")}>
                {t("overview.topAsins.columns.sdSales")}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[100px]")}>
                {t("overview.topAsins.columns.adSpend")}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[76px]")}>
                {t("overview.topAsins.columns.acos")}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[96px] pr-5")}>
                {t("overview.topAsins.columns.tacos")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [0, 1, 2, 3, 4].map((row) => (
                <TableRow key={row} className="h-14 border-line hover:bg-transparent">
                  <TableCell colSpan={8} className="px-5">
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow className="h-14 hover:bg-transparent">
                <TableCell colSpan={8} className="px-5 text-sm text-muted-foreground">
                  {t("overview.topAsins.empty")}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const label = row.title ?? row.asin;
                const overTarget =
                  targetAcos !== null && row.acos !== null && row.acos > targetAcos * 1.25;
                return (
                  <TableRow
                    key={row.key}
                    className="group/row h-14 border-line hover:bg-table-row-hover"
                  >
                    <TableCell className="sticky left-0 z-[1] bg-surface py-2 pr-0 pl-5 group-hover/row:bg-linear-to-r group-hover/row:from-table-row-hover group-hover/row:to-table-row-hover">
                      <span className="flex min-w-0 items-center gap-2.5">
                        <ProductThumbnail
                          imageUrl={row.imageUrl}
                          label={label}
                          asin={row.asin}
                          size="compact"
                        />
                        <span className="flex min-w-0 flex-col gap-0.75">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <Link
                              to="/all-products"
                              search={{
                                dimension: row.isParent ? "PARENT_ASIN" : "ASIN",
                                search: row.asin,
                              }}
                              className="truncate rounded-xs text-xs font-medium text-ink underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-focus-ring"
                            >
                              {label}
                            </Link>
                            {row.isParent ? (
                              <span className="inline-flex h-4.5 shrink-0 items-center rounded-full bg-info-background px-1.5 text-badge font-medium text-info-foreground">
                                {t("overview.topAsins.parent")}
                              </span>
                            ) : null}
                          </span>
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className="text-badge tabular-nums text-text-secondary">
                              {row.asin}
                            </span>
                            {row.marketplaces.length > 0 ? (
                              <span className="flex gap-0.75">
                                {row.marketplaces.map((code) => (
                                  <MarketplaceBadge key={code} size="sm" code={code} />
                                ))}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className={CELL}>{money(row.totalSales)}</TableCell>
                    <TableCell className={CELL}>
                      {row.units === null
                        ? "—"
                        : formatNumber(row.units, { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell className={cn(CELL, HIDE_P3)}>{money(row.spSales)}</TableCell>
                    <TableCell className={cn(CELL, HIDE_P3)}>{money(row.sdSales)}</TableCell>
                    <TableCell className={CELL}>{money(row.adSpend)}</TableCell>
                    <TableCell className={cn(CELL, overTarget && "text-destructive-foreground")}>
                      {percent(row.acos)}
                    </TableCell>
                    <TableCell className={cn(CELL, "pr-5")}>{percent(row.tacos)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      )}
    </DashboardCard>
  );
}
